import { Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { validateIBAN, validateIBANPortugal } from '@commons';
import { Campaign, GenericResponse, Mobile, Select } from '@interfaces';
import { ToastrService } from '@shared/ui/toastr/toastr.service';
import { I18NEXT_SERVICE } from 'angular-i18next';
import { forkJoin, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/auth';
import { UtilValidatorsService } from 'src/app/commons/validations-format/util-validators.service';
import { CampaignDetail, Fee } from 'src/app/modules/campaigns/models/campaign.models';
import { NewContractPymesService } from 'src/app/services/new-contract-pymes.service';
import { UtilService } from 'src/app/services/util.service';
import { EnterMargins, RenovateContractFormPymes, RenovateContractPymes, RenovateNextSaveData } from '../../models/renovate-contract-pymes.model';
import { RenovateContractPymesService } from '../../services/renovate-contract-pymes.service';

@Component({
  selector: 'app-renovate-contract-pymes-form',
  templateUrl: './renovate-contract-pymes-form.component.html',
  styleUrls: [ './renovate-contract-pymes-form.component.scss' ]
})
export class RenovateContractPymesFormComponent implements OnInit, OnDestroy {
  public approved = false;
  public callQuery = false;
  private idDraft: number;
  public campaignList: Select[] = [];
  public allCampaigns = [];
  public levelList: Select[] = [];
  public viaTypesList: Select[] = [];
  public townList: Select[] = [];
  public renovateForm: FormGroup;
  private destroy$ = new Subject();
  public fixPrice: boolean;
  public canSettlements: boolean;
  public resultCalculation = true;
  public regexTwoDecimal = new RegExp(/^(\d{1,2}(\,\d{0,3})?|\.?\d{1,2})$/g);
  public regexSixDecimal = new RegExp(/^(\d+(\,\d{0,6})?|\,?\d{1,3})$/g);
  public showCalculation = false;
  public resultTP = [];
  public resultTE = [];
  public campaignRealTE = [];
  public campaignAI = [];
  public campaignKI = [];
  public unitsTE: Select[] = [
    { value: '0', label: '€ / kWh' },
    { value: '100', label: 'ctms / kWh' },
    { value: '1000', label: '€ / MWh' }
  ];
  public unitsTP: Select[] = [];
  public resultAI = [];
  public nextDate = new Date();
  private campaignTE = [];
  private campaignTP = [];
  private commissionTE: number;
  private commissionTP: number;
  public commission = 0;
  private destroyed$ = new Subject<boolean>();
  public isUserSpanish = true;
  public passThrough: boolean;
  public fijoFlex: boolean;
  public selectedCampaignFee: Fee;
  public greaterEqual100: boolean;
  public campaignFI = [];
  public campaignCG = [];
  public campaignGDOS = [];
  public campaignPFijo = [];
  public mobileCountryCodeList: Select[];
  public campaignDetail: CampaignDetail[];
  public selectedCampaign: Campaign;
  public enterMarginsDraft: EnterMargins;
  public showBankAccountFields = false;
  private enterMarginsValid = false;

  @Input() dataContract: RenovateContractPymes;
  @Output() canSave: EventEmitter<RenovateNextSaveData> = new EventEmitter();

  constructor(
    private newContractPymesService: NewContractPymesService,
    private renovateContractPymesService: RenovateContractPymesService,
    private utilService: UtilService,
    private auth: AuthService,
    private toastrService: ToastrService,
    @Inject(I18NEXT_SERVICE) private i18NextService,
    private utilValidatorsService: UtilValidatorsService,
  ) { }

  ngOnInit(): void {
    this.isUserSpanish = this.auth.getUserData().country === 'ES';
    if (this.dataContract.contract.idDraft) {
      this.idDraft = this.dataContract.contract.idDraft;
      this.approved = this.dataContract.campaign.approved;
      this.enterMarginsDraft = this.dataContract.campaign.enterMargins;
      this.callQuery = true;
    }
    this.canSettlements = this.auth.getUserData().hasAccessToSettlement;
    this.commissionTE = this.auth.getUserData().comisionCanalEnergia ?
      parseFloat(this.auth.getUserData().comisionCanalEnergia) : 0;
    this.commissionTP = this.auth.getUserData().comisionCanalPotencia ?
      parseFloat(this.auth.getUserData().comisionCanalPotencia) : 0;
    this.subscribeLanguajeChanges();
    if (this.isUserSpanish) {
      this.createForm();
    } else {
      this.initMobileCountryCodeConfigPortugal();
    }
    this.getDataDropdownsInit();
    this.subscribePaymentChanges();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private subscribeLanguajeChanges(): void {
    this.i18NextService.events.languageChanged.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.unitsTP = [
        { value: '0', label: this.i18NextService.t('NEW_CONTRACTS.KW_YEAR') },
        { value: '12', label: this.i18NextService.t('NEW_CONTRACTS.KW_MONTH') },
        { value: '365', label: this.i18NextService.t('NEW_CONTRACTS.KW_DAY') }
      ];
    });
  }

  private createForm(): void {
    const phoneAndCountryCode = this.getMobileAndCountryCode(this.dataContract.socialReason.phoneContact);
    const phoneSignerAndCountryCode = this.getMobileAndCountryCode(this.dataContract.contract.phoneSigner);
    this.renovateForm = new FormGroup({
      fee: new FormControl({ value: this.dataContract.installation.feeLabel, disabled: true }),
      campaign: new FormControl({ value: this.dataContract.campaign.campaign, disabled: true }, Validators.required),
      level: new FormControl({ value: this.dataContract.campaign.level, disabled: true }, Validators.required),
      viaType: new FormControl({ value: this.dataContract.contract.viaType, disabled: true }, Validators.required),
      roadName: new FormControl(
        { value: this.dataContract.contract.roadName, disabled: true },
        [ Validators.required, Validators.maxLength(100) ]
      ),
      number: new FormControl({ value: this.dataContract.contract.number, disabled: true }, [ Validators.maxLength(8), Validators.pattern(this.utilValidatorsService.alphaNumericPattern) ]),
      gate: new FormControl({ value: this.dataContract.contract.gate, disabled: true }, Validators.maxLength(10)),
      stair: new FormControl({ value: this.dataContract.contract.stair, disabled: true }, [ Validators.maxLength(3), Validators.pattern(this.utilValidatorsService.alphaNumericPattern) ]),
      floor: new FormControl({ value: this.dataContract.contract.floor, disabled: true }, [ Validators.maxLength(3), Validators.pattern(this.utilValidatorsService.alphaNumericPattern) ]),
      door: new FormControl({ value: this.dataContract.contract.door, disabled: true }, [ Validators.maxLength(3), Validators.pattern(this.utilValidatorsService.alphaNumericPattern) ]),
      postalCode: new FormControl(
        { value: this.dataContract.contract.postalCode, disabled: true },
        [ Validators.required, Validators.minLength(5) ]
      ),
      postalCodePT1: new FormControl({ value: this.isUserSpanish ? null : this.dataContract.contract.postalCode.slice(0, 4), disabled: true }),
      postalCodePT2: new FormControl({ value: this.isUserSpanish ? null : this.dataContract.contract.postalCode.slice(5, 8), disabled: true }),
      town: new FormControl({ value: this.dataContract.contract.town, disabled: true }, Validators.required),
      province: new FormControl({ value: null, disabled: true }, [ Validators.required, Validators.maxLength(100) ]),
      name: new FormControl(
        { value: this.dataContract.socialReason.nameContact, disabled: true },
        [ Validators.required, Validators.maxLength(50) ]
      ),
      surname: new FormControl(
        { value: this.dataContract.socialReason.surnameContact, disabled: true },
        [ Validators.required, Validators.maxLength(50) ]
      ),
      dni: new FormControl(
        { value: this.dataContract.socialReason.dniContact, disabled: true },
        [ Validators.required, this.utilValidatorsService.validateDNI ]
      ),
      email: new FormControl(
        { value: this.dataContract.socialReason.emailContact, disabled: true },
        [ Validators.required, Validators.pattern(this.utilValidatorsService.emailPattern), Validators.maxLength(50) ]
      ),
      phone: new FormControl(
        { value: this.isUserSpanish ? this.dataContract.socialReason.phoneContact : phoneAndCountryCode.mobile, disabled: true },
        [ Validators.required, this.utilValidatorsService.validateMobile, Validators.maxLength(9) ]
      ),
      phoneCountryCode: new FormControl(
        { value: this.isUserSpanish ? null : phoneAndCountryCode.countryCode, disabled: true },
        this.isUserSpanish ? [] : [ Validators.required ]
      ),
      nameSigner: new FormControl(
        { value: this.dataContract.contract.nameSigner, disabled: true },
        [ Validators.required, Validators.maxLength(50) ]
      ),
      surnameSigner: new FormControl(
        { value: this.dataContract.contract.surnameSigner, disabled: true },
        [ Validators.required, Validators.maxLength(50) ]
      ),
      dniSigner: new FormControl(
        { value: this.dataContract.contract.dniSigner, disabled: true },
        [ Validators.required, this.utilValidatorsService.validateDNI ]
      ),
      emailSigner: new FormControl(
        { value: this.dataContract.contract.emailSigner, disabled: true },
        [ Validators.required, Validators.pattern(this.utilValidatorsService.emailPattern), Validators.maxLength(50) ]
      ),
      phoneSigner: new FormControl(
        { value: this.isUserSpanish ? this.dataContract.contract.phoneSigner : phoneSignerAndCountryCode.mobile, disabled: true },
        [ Validators.required, this.utilValidatorsService.validateMobile, Validators.maxLength(9) ]
      ),
      phoneSignerCountryCode: new FormControl(
        { value: this.isUserSpanish ? null : phoneSignerAndCountryCode.countryCode, disabled: true },
        this.isUserSpanish ? [] : [ Validators.required ]
      ),
      iban: new FormControl({ value: this.dataContract.contract.iban, disabled: true }),
      entity: new FormControl({ value: this.dataContract.contract.entity, disabled: true }),
      office: new FormControl({ value: this.dataContract.contract.office, disabled: true }),
      dc: new FormControl({ value: this.dataContract.contract.dc, disabled: true }),
      account: new FormControl({ value: this.dataContract.contract.account, disabled: true }),
      campaignBase: new FormControl(this.dataContract.campaign.campaignBase),
      marginIndicator: new FormControl(this.dataContract.campaign.marginIndicator),
      unitsTE: new FormControl(this.dataContract.campaign.unitsTE || '0'),
      unitsTP: new FormControl(this.dataContract.campaign.unitsTP || '0'),
      enterMargins: this.enterMargins(),
      calculateMargins: this.calculateMargins(),
      payment: new FormControl(
        { value: this.dataContract.campaign?.isMultibanco ? '1' : '0', disabled: true },
        this.isUserSpanish ? Validators.required : null),
    });
    this.renovateForm.setValidators(this.isUserSpanish ? this.validateIBANControls : null);
    if (!this.isUserSpanish) {
      this.mobileCountryCodeChange(this.renovateForm.get('phoneCountryCode').value, 'phone');
      this.mobileCountryCodeChange(this.renovateForm.get('phoneSignerCountryCode').value, 'phoneSigner');
    }
  }

  private enterMargins(): FormGroup {
    if (this.isUserSpanish) {
      return new FormGroup({
        marginsTE: new FormGroup({
          p1: new FormControl(this.dataContract.campaign.enterMargins.marginsTE?.p1),
          p2: new FormControl(this.dataContract.campaign.enterMargins.marginsTE?.p2),
          p3: new FormControl(this.dataContract.campaign.enterMargins.marginsTE?.p3),
          p4: new FormControl(this.dataContract.campaign.enterMargins.marginsTE?.p4),
          p5: new FormControl(this.dataContract.campaign.enterMargins.marginsTE?.p5),
          p6: new FormControl(this.dataContract.campaign.enterMargins.marginsTE?.p6)
        }),
        marginTP: new FormControl(this.dataContract.campaign.enterMargins.marginTP)
      });
    } else {
      return new FormGroup({
        marginsTE: new FormGroup({
          p1: new FormControl(this.dataContract.campaign.enterMargins.marginsTE?.p1),
          p2: new FormControl(this.dataContract.campaign.enterMargins.marginsTE?.p2),
          p3: new FormControl(this.dataContract.campaign.enterMargins.marginsTE?.p3),
          p4: new FormControl(this.dataContract.campaign.enterMargins.marginsTE?.p4),
          p5: new FormControl(this.dataContract.campaign.enterMargins.marginsTE?.p5),
          p6: new FormControl(this.dataContract.campaign.enterMargins.marginsTE?.p6)
        }),
      });
    }
  }

  private calculateMargins(): FormGroup {
    return new FormGroup({
      marginTP: new FormGroup({
        p1: new FormControl(this.dataContract.campaign.calculateMargins.marginTP.p1),
        p2: new FormControl(this.dataContract.campaign.calculateMargins.marginTP.p2),
        p3: new FormControl(this.dataContract.campaign.calculateMargins.marginTP.p3),
        p4: new FormControl(this.dataContract.campaign.calculateMargins.marginTP.p4),
        p5: new FormControl(this.dataContract.campaign.calculateMargins.marginTP.p5),
        p6: new FormControl(this.dataContract.campaign.calculateMargins.marginTP.p6)
      }),
      marginTE: new FormGroup({
        p1: new FormControl(this.dataContract.campaign.calculateMargins.marginTE.p1),
        p2: new FormControl(this.dataContract.campaign.calculateMargins.marginTE.p2),
        p3: new FormControl(this.dataContract.campaign.calculateMargins.marginTE.p3),
        p4: new FormControl(this.dataContract.campaign.calculateMargins.marginTE.p4),
        p5: new FormControl(this.dataContract.campaign.calculateMargins.marginTE.p5),
        p6: new FormControl(this.dataContract.campaign.calculateMargins.marginTE.p6)
      })
    });
  }

  private subscribeCondition(firstTime: boolean): void {
    if (firstTime) {
      this.subscribeForm();
      this.emitValuesSaveContact();
    }
  }

  private subscribeForm(): void {
    this.renovateForm.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      this.emitValuesSaveContact();
    });
    this.subscribePostalCode();
    this.subscribeToCalculateCommssionChange();
    this.subscribeCampaign();
    if (!this.isUserSpanish) {
      this.subscribeFormControlPortugal();
    }
  }

  private subscribePostalCode(): void {
    this.renovateForm.get('postalCode').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
      this.renovateForm.get('town').setValue(null);
      this.renovateForm.get('province').setValue(null);
      this.townList = [];
      if (value && ((this.isUserSpanish && value.length > 4) || (!this.isUserSpanish && value.length > 7))) {
        this.getCodigoPostal();
      }
    });
  }

  private getCodigoPostal(): void {
    this.renovateContractPymesService.setLoader(true);
    this.newContractPymesService.getCodigoPostal(this.renovateForm.get('postalCode').value).pipe(takeUntil(this.destroyed$)).subscribe(
      (response: GenericResponse) => {
        this.postalCodeResponse(response);
        this.renovateContractPymesService.setLoader(false);
      },
      error => {
        this.renovateContractPymesService.setLoader(false);
        this.utilService.toastErrors(error);
      }
    );
  }

  private subscribeToCalculateCommssionChange(): void {
    this.renovateForm.get('marginIndicator').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
      if (this.renovateForm.get('campaignBase').value) {
        this.showCalculation = false;
        this.resetAllFields();
        if (value === '2') {
          this.setValidatorFieldRequired(this.renovateForm.get('calculateMargins.marginTE'));
          this.setValidatorFieldRequired(this.renovateForm.get('calculateMargins.marginTP'));
          this.setFielsPendingFee();
        }
      }
    });
  }

  private subscribeCampaign(): void {
    this.renovateForm.get('campaign').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((campaign) => {
      if (campaign) {
        this.enterMarginsDraft = null;
        this.renovateForm.get('level').enable({ emitEvent: false });
        this.selectedCampaign = this.allCampaigns.find(el => el.codCampaña === campaign);
        this.renovateForm.get('campaignBase').setValue(this.selectedCampaign.campanaBase);
        this.renovateForm.get('level').setValue(null);
        this.passThrough = this.allCampaigns.find(item => item.codCampaña === campaign)?.passThrough;
        this.fijoFlex = this.allCampaigns.find(item => item.codCampaña === campaign)?.fijoFlex;
        if (this.selectedCampaign.campanaBase) {
          this.fixPrice = this.selectedCampaign.precioFijo;
          this.getCampaignDetail(this.selectedCampaign.idCampaña);
          this.renovateForm.get('level').disable();
        } else {
          this.renovateForm.get('level').enable();
          this.getLevels(campaign);
        }
        this.showCalculation = false;
        this.commission = null;
        this.resetAllFields();
        this.renovateForm.get('marginIndicator').setValue('1');
        if (!this.isUserSpanish) {
          this.renovateForm.get('payment').setValue(this.selectedCampaign?.formPago === '300000007' ? '1' : '0', { emitEvent: false });
          this.enablePayment(this.selectedCampaign?.formPago === '300000008');
          this.showBankAccount(
            (this.selectedCampaign?.formPago !== '300000007' && this.selectedCampaign?.formPago !== '300000008') ||
            (this.selectedCampaign?.formPago === '300000008' && this.renovateForm.get('payment').value === '0')
          );
        }
      }
    });
  }

  private getServiceCampaignsFiltered(): Observable<GenericResponse<Campaign[]>> {
    if (this.isUserSpanish) {
      return this.renovateContractPymesService.getCampaignsFiltered(
        this.auth.getUserData().contactoCRM,
        this.renovateForm.get('fee').value,
        this.dataContract.campaign.geographicZone,
        this.dataContract.campaign.estimateConsuption.toString(),
        this.dataContract.contract.codContract
      );
    } else {
      return this.newContractPymesService.getCampaignsFilteredPT(
        this.auth.getUserData().contactoCRM,
        this.dataContract.installation.tensionId?.toString(),
        this.dataContract.installation.cycleId?.toString(),
        this.dataContract.campaign.estimateConsuption?.toString(),
        this.dataContract.installation.periodId?.toString(),
      );
    }
  }

  private getDataDropdownsInit(): void {
    this.renovateContractPymesService.setLoader(true);
    const services = [
      this.newContractPymesService.getTipoVia(),
      this.getServiceCampaignsFiltered()
    ];
    if (
      this.dataContract.contract.postalCode &&
      ((this.isUserSpanish && this.dataContract.contract.postalCode.length > 4) || (!this.isUserSpanish && this.dataContract.contract.postalCode.length > 7))
    ) {
      services.push(this.newContractPymesService.getCodigoPostal(this.renovateForm.get('postalCode').value));
    }
    forkJoin(services).subscribe(
      results => {
        if (results[ 0 ].success) {
          this.viaTypesList = results[ 0 ].data.map(res => ({ label: res.nombreTipoVia, value: res.nombreTipoVia }));
        } else {
          this.viaTypesList = [];
          this.utilService.toastErrors(results[ 0 ]);
        }
        if (
          this.dataContract.contract.postalCode &&
          ((this.isUserSpanish && this.dataContract.contract.postalCode.length > 4) || (!this.isUserSpanish && this.dataContract.contract.postalCode.length > 7))
        ) {
          this.postalCodeResponse(results[ 2 ], true);
        }
        if (this.idDraft) {
          this.enableForm();
        }
        this.campaignResponse(results[ 1 ], true);
        this.renovateContractPymesService.setLoader(false);
      },
      error => {
        this.utilService.toastErrors(error);
        this.renovateContractPymesService.setLoader(false);
      }
    );
  }

  private validateIBANControls(control: AbstractControl): ValidationErrors | null {
    const iban = control.get('iban').value ? control.get('iban').value : null;
    const entity = control.get('entity').value ? control.get('entity').value : null;
    const office = control.get('office').value ? control.get('office').value : null;
    const dc = control.get('dc').value ? control.get('dc').value : null;
    const account = control.get('account').value ? control.get('account').value : null;
    if (!validateIBAN(iban + entity + office + dc + account)) {
      return { invalid_IBAN: true };
    }
    return null;
  }

  private campaignResponse(response: GenericResponse<Campaign[]>, firstTime = false): void {
    if (response.success) {
      this.campaignList = this.mapCampaign(response.data);
      this.allCampaigns = response.data;
      this.levelList = [];
      if (this.campaignList.length === 0) {
        this.toastrService.error('NEW_CONTRACTS.NO_CAMPAIGNS_ERROR');
        this.resetErrorCampaign();
      } else {
        if (!this.campaignList.find(campaign => campaign.value === this.renovateForm.get('campaign').value)) {
          this.resetErrorCampaign();
        }
        const campaignValue = this.renovateForm.get('campaign').value;
        this.selectedCampaign = this.allCampaigns.find(campaign => campaign.codCampaña === campaignValue);
        this.passThrough = this.selectedCampaign?.passThrough;
        if (!this.isUserSpanish) {
          this.enablePayment(this.selectedCampaign?.formPago === '300000008');
          this.showBankAccount(
            (this.selectedCampaign?.formPago !== '300000007' && this.selectedCampaign?.formPago !== '300000008') ||
            (this.selectedCampaign?.formPago === '300000008' && this.renovateForm.get('payment').value === '0')
          );
        }
        this.fijoFlex = this.selectedCampaign?.fijoFlex;
        if (campaignValue) {
          if (this.selectedCampaign.campanaBase) {
            this.renovateForm.get('campaignBase').setValue(this.selectedCampaign.campanaBase);
            this.fixPrice = this.selectedCampaign.precioFijo;
            if (this.renovateForm.get('marginIndicator').value !== '1') {
              this.setFielsPendingFee();
              this.setValidatorFieldRequired(this.renovateForm.get('calculateMargins.marginTE'));
              this.setValidatorFieldRequired(this.renovateForm.get('calculateMargins.marginTP'));
            }
            this.renovateForm.get('level').disable();
            this.getCampaignDetail(this.selectedCampaign.idCampaña, firstTime);
          } else {
            this.getLevels(campaignValue, firstTime);
            this.subscribeCondition(firstTime);
          }
        } else {
          this.renovateForm.get('level').disable();
          this.subscribeCondition(firstTime);
        }
      }
    } else {
      this.campaignList = [];
      this.levelList = [];
      this.resetErrorCampaign();
      this.utilService.toastErrors(response);
    }
  }

  private getLevels(event: string, firstTime?: boolean): void {
    if (!firstTime) {
      this.renovateForm.get('level').enable();
    }

    const campaignSelected = this.allCampaigns.find(el => el.codCampaña === event);
    this.levelList = campaignSelected.niveles.map(res2 => ({ label: res2.nombreNivel, value: res2.nombreNivel }));
    if (!this.levelList.find(level => level.value === this.renovateForm.get('level').value)) {
      this.renovateForm.get('level').setValue(null);
    }
  }

  private getCampaignDetail(idCampaign: number, firstTime = false): void {
    this.renovateContractPymesService.setLoader(true);
    this.renovateContractPymesService.getCampaignDetail(idCampaign).pipe(takeUntil(this.destroyed$)).subscribe(
      (response: GenericResponse<CampaignDetail[]>) => {
        if (response.success) {
          this.campaignDetail = response.data;
          this.findFee();
          this.doCalculate();
          this.subscribeCondition(firstTime);
        } else {
          this.campaignTE = [];
          this.campaignTP = [];
          this.campaignAI = [];
          this.campaignKI = [];
          this.campaignFI = [];
          this.campaignCG = [];
          this.campaignGDOS = [];
          this.campaignPFijo = [];          
          this.subscribeCondition(firstTime);
          this.utilService.toastErrors(response);
        }
        this.renovateContractPymesService.setLoader(false);
      }, error => {
        this.subscribeCondition(firstTime);
        this.renovateContractPymesService.setLoader(false);
        this.utilService.toastErrors(error);
      }
    );
  }

  private doCalculate(): void {
    if (Object.values(this.renovateForm.get('calculateMargins.marginTE').value).every(val => val) &&
      Object.values(this.renovateForm.get('calculateMargins.marginTP').value).every(val => val)) {
      this.calculateMarginMatchPrice();
    }
  }

  private mapCampaign(values: Campaign[]): Select[] {
    const aux = [];
    values.forEach(element => {
      if (
        (this.isUserSpanish && !element.tarifas.every(tarifa => tarifa.nombreTarifa !== this.renovateForm.get('fee').value)) ||
        !this.isUserSpanish
      ) {
        aux.push({ label: element.nombreCampaña, value: element.codCampaña });
      }
    });
    return aux;
  }

  private resetErrorCampaign(): void {
    this.renovateForm.get('campaign').setValue(null);
    this.renovateForm.get('level').setValue(null);
    this.renovateForm.get('level').disable();
    this.renovateForm.get('campaignBase').setValue(false);
    this.renovateForm.get('marginIndicator').setValue('1');
    this.resetAllFields();
  }

  private setValidatorFieldRequired(form: any): void {
    Object.keys(form.controls).forEach(field => {
      if ((field === 'marginTE') || (field === 'marginsTE')) {
        this.greaterEqual100 = this.dataContract.campaign.estimateConsuption >= 100000;
        form.get(field).setValidators([ Validators.required, Validators.min(0), Validators.max(this.isUserSpanish ? (this.greaterEqual100 ? 24.999 : 49.999) : 25.999), Validators.pattern('([0-9]{1,2}(,[0-9]{1,3})?)') ]);
      } else if (field === 'marginTP') {
        form.get(field).setValidators([ Validators.required, Validators.min(0), Validators.max(24.999), Validators.pattern('([0-9]{1,2}(,[0-9]{1,3})?)') ]);
      } else {
        form.get(field).setValidators([ Validators.required ]);
      }
      form.get(field).updateValueAndValidity({ emitEvent: false });
    });
  }

  private setFielsPendingFee(): void {
    if (this.renovateForm.get('fee').value === '2.0TD') {
      this.setDisabledField(this.renovateForm.get('calculateMargins.marginTE'), 3);
    } else {
      this.setEnabledField(this.renovateForm.get('calculateMargins.marginTE'));
    }
    if (this.renovateForm.get('fee').value === '2.0TD') {
      this.setDisabledField(this.renovateForm.get('calculateMargins.marginTP'), 2);
    } else {
      this.setEnabledField(this.renovateForm.get('calculateMargins.marginTP'));
    }
  }

  private setDisabledField(form: any, indexControl: number): void {
    Object.keys(form.controls).forEach((field, index) => {
      if (index + 1 > indexControl) {
        form.get(field).disable();
        form.get(field).setValue(0);
        form.get(field).clearValidators();
      }
      form.get(field).updateValueAndValidity();
    });
  }

  private setEnabledField(form: any): void {
    Object.keys(form.controls).forEach(field => {
      form.get(field).enable({ emitEvent: false });
      form.get(field).updateValueAndValidity({ emitEvent: false });
    });
  }

  private resetAllFields(): void {
    this.resetField(this.renovateForm.get('enterMargins.marginsTE'));
    if (this.isUserSpanish) {
      this.renovateForm.get('enterMargins.marginTP')?.reset();
      this.renovateForm.get('enterMargins.marginTP')?.clearValidators();
      this.renovateForm.get('enterMargins.marginTP')?.setValue(null);
      this.renovateForm.get('enterMargins.marginTP')?.updateValueAndValidity();
    }
    this.resetField(this.renovateForm.get('calculateMargins.marginTE'));
    this.resetField(this.renovateForm.get('calculateMargins.marginTP'));
    this.renovateForm.get('unitsTE')?.setValue('0', { emitEvent: false });
    this.renovateForm.get('unitsTP')?.setValue('0', { emitEvent: false });
    this.renovateForm.get('unitsTE')?.updateValueAndValidity({ emitEvent: false });
    this.renovateForm.get('unitsTP')?.updateValueAndValidity({ emitEvent: false });
    this.renovateForm.get('enterMargins.marginsTE')?.updateValueAndValidity();
    this.renovateForm.get('enterMargins.marginTP')?.updateValueAndValidity();
    this.renovateForm.get('calculateMargins.marginTE')?.updateValueAndValidity();
    this.renovateForm.get('calculateMargins.marginTP')?.updateValueAndValidity();
  }

  private resetField(form: any): void {
    Object.keys(form.controls)?.forEach(field => {
      form.get(field)?.reset();
      form.get(field)?.clearValidators();
      form.get(field)?.setValue(null);
      form.get(field)?.updateValueAndValidity();
    });
  }

  private postalCodeResponse(response: GenericResponse, firstTime = false): void {
    if (response.success) {
      this.townList = response.data?.poblaciones?.map(value => ({
        label: value.nombrePoblacion,
        value: value.codigoPoblacion
      })) || [];
      if (this.townList.length <= 1) {
        this.renovateForm.get('town').setValue(this.townList[ 0 ]?.label);
        this.renovateForm.get('town').disable({ emitEvent: false });
      } else {
        if (!firstTime) {
          this.renovateForm.get('town').setValue(null);
        }
        if (this.approved) {
          this.renovateForm.get('town').enable({ emitEvent: false });
        }
      }
      if (response.data?.nombreProvincia) {
        this.renovateForm.get('province').setValue(response.data?.nombreProvincia);
      }
      this.renovateForm.updateValueAndValidity();
    } else {
      this.townList = [];
      this.utilService.toastErrors(response);
    }
  }

  public handleQuery(): void {
    this.renovateContractPymesService.setLoader(true);
    const params = {
      CodeCIF: this.dataContract.campaign.cif,
      CodeCUPs: this.dataContract.campaign.cups,
      ContactoCRM: this.auth.getUserData().contactoCRM,
      ConsumoEstimado: this.dataContract.campaign.estimateConsuption,
      TarifaSips: this.renovateForm.get('fee').value,
      ZonaGeograficaCUPS: this.dataContract.campaign.geographicZone
    };
    if (!this.isUserSpanish) {
      (params as any).IdTension = this.dataContract.installation.tensionId;
      (params as any).TensionCUPS = this.dataContract.installation.tension;
      (params as any).IdCiclo = this.dataContract.installation.cycleId;
      (params as any).CicloCUPS = this.dataContract.installation.cycle;
      (params as any).IdPeriodo = this.dataContract.installation.periodId;
      (params as any).PeriodoCUPS = this.dataContract.installation.period;
    }
    this.renovateContractPymesService.queryCifRenovate(
      this.dataContract.contract.codContract,
      params
    ).subscribe(
      response => {
        if (response.success) {
          this.callQuery = true;
          this.approved = response.data.aprobado;
          this.idDraft = response.data.idBorrador;
          if (this.approved && this.idDraft) {
            this.enableForm();
          } else {
            Object.keys(this.renovateForm.controls).forEach(element => {
              this.renovateForm.get(element).disable({ emitEvent: false });
            });
          }
          this.renovateForm.updateValueAndValidity();
        } else {
          this.callQuery = false;
          this.approved = false;
          delete this.idDraft;
          this.utilService.toastErrors(response);
        }
        this.renovateContractPymesService.setLoader(false);
      },
      error => {
        this.callQuery = false;
        this.approved = false;
        delete this.idDraft;
        this.utilService.toastErrors(error);
        this.renovateContractPymesService.setLoader(false);
      }
    );
  }

  private enableForm(): void {
    Object.keys(this.renovateForm.controls).forEach(element => {
      if (element !== 'province' && element !== 'town' && element !== 'campaign' && element !== 'level' && element !== 'fee'
        && element !== 'calculateMargins' && element !== 'enterMargins' && element !== 'payment') {
        this.renovateForm.get(element).enable({ emitEvent: false });
      } else if (
        (element === 'town' && this.townList?.length > 1) ||
        (element === 'campaign' && this.renovateForm.get('fee').value) ||
        (element === 'level' && this.renovateForm.get('campaign').value)
      ) {
        this.renovateForm.get(element).enable({ emitEvent: false });
      }
    });
    this.emitValuesSaveContact();
  }

  public getLevelList(event: string): void {
    this.allCampaigns.forEach(res => {
      if (res.codCampaña === event) {
        this.levelList = res.niveles.map(res2 => ({ label: res2.nombreNivel, value: res2.nombreNivel }));
      }
    });
  }

  public copyContactToSigner(event): void {
    if (event.checked) {
      this.renovateForm.get('nameSigner').disable();
      this.renovateForm.get('nameSigner').setValue(this.renovateForm.get('name').value);
      this.renovateForm.get('name').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.renovateForm.get('nameSigner').setValue(this.renovateForm.get('name').value);
      });
      this.renovateForm.get('surnameSigner').disable();
      this.renovateForm.get('surnameSigner').setValue(this.renovateForm.get('surname').value);
      this.renovateForm.get('surname').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.renovateForm.get('surnameSigner').setValue(this.renovateForm.get('surname').value);
      });
      this.renovateForm.get('dniSigner').disable();
      this.renovateForm.get('dniSigner').setValue(this.renovateForm.get('dni').value);
      this.renovateForm.get('dni').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.renovateForm.get('dniSigner').setValue(this.renovateForm.get('dni').value);
      });
      this.renovateForm.get('emailSigner').disable();
      this.renovateForm.get('emailSigner').setValue(this.renovateForm.get('email').value);
      this.renovateForm.get('email').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.renovateForm.get('emailSigner').setValue(this.renovateForm.get('email').value);
      });
      this.renovateForm.get('phoneSigner').disable();
      this.renovateForm.get('phoneSigner').setValue(this.renovateForm.get('phone').value);
      this.renovateForm.get('phone').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.renovateForm.get('phoneSigner').setValue(this.renovateForm.get('phone').value);
      });
      this.renovateForm.get('phoneSignerCountryCode').disable();
      this.renovateForm.get('phoneSignerCountryCode').setValue(this.renovateForm.get('phoneCountryCode').value);
      this.renovateForm.get('phoneCountryCode').valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.renovateForm.get('phoneSignerCountryCode').setValue(this.renovateForm.get('phoneCountryCode').value);
      });
    } else {
      this.renovateForm.get('nameSigner').enable();
      this.renovateForm.get('surnameSigner').enable();
      this.renovateForm.get('dniSigner').enable();
      this.renovateForm.get('emailSigner').enable();
      this.renovateForm.get('phoneSigner').enable();
      this.renovateForm.get('phoneSignerCountryCode').enable();
      this.destroy$.next();
      this.destroy$.complete();
    }
  }

  private findFee(): void {
    let feeName = this.isUserSpanish ? this.renovateForm.get('fee').value : this.dataContract?.installation?.feeLabel;
    if (feeName.includes('S/C - ')) {
      feeName = feeName.replace('S/C - ', '');
    }
    const findFee = this.campaignDetail.find(a => a.zonasGeograficas.find(
      zone => zone.nombreZona === this.dataContract.campaign.geographicZone
    ).tarifas.find(b => b.nombreTarifa === feeName));
    const powerTerms = findFee?.zonasGeograficas.find(
      zone => zone.nombreZona === this.dataContract.campaign.geographicZone
    ).tarifas.find(el => el.nombreTarifa === feeName);
    this.selectedCampaignFee = powerTerms;
    this.campaignTE = [];
    this.campaignTP = [];
    this.campaignAI = [];
    this.campaignKI = [];
    this.campaignFI = [];
    this.campaignCG = [];
    this.campaignGDOS = [];
    this.campaignPFijo = [];
    const pLenght = this.isUserSpanish ? 6 : 4;
    for (let i = 0; i < pLenght; i++) {
      let priceTE = 0;
      if (!this.isUserSpanish && this.selectedCampaign.precioFijo) {
        priceTE = !powerTerms[`precio_P${i + 1}`] ? 0 : powerTerms[`precio_P${i + 1}`];
      } else {
        priceTE = !powerTerms[`precioFinal_P${i + 1}`] ? 0 : powerTerms[`precioFinal_P${i + 1}`];
      }
      const priceRealTE = !powerTerms[ `tE_P${i + 1}` ] ? 0 : powerTerms[ `tE_P${i + 1}` ];
      const priceTP = powerTerms[ `tP_P${i + 1}` ] === null ? null : powerTerms[ `tP_P${i + 1}` ];
      const priceAI = !powerTerms[ `ai_P${i + 1}` ] ? 0 : powerTerms[ `ai_P${i + 1}` ];
      const priceKI = !powerTerms[ `ki_P${i + 1}` ] ? 0 : powerTerms[ `ki_P${i + 1}` ];
      this.campaignTE.push(priceTE);
      this.campaignRealTE.push(priceRealTE / 1000);
      this.campaignTP.push(priceTP);
      this.campaignAI.push(priceAI);
      this.campaignKI.push(priceKI);
      this.campaignFI.push(!powerTerms[ `fi_P${i + 1}` ] ? null : powerTerms[ `fi_P${i + 1}` ]);
      this.campaignCG.push(!powerTerms[ `cG_P${i + 1}` ] ? null : powerTerms[ `cG_P${i + 1}` ]);
      this.campaignGDOS.push(!powerTerms[ `gdOs_P${i + 1}` ] ? null : powerTerms[ `gdOs_P${i + 1}` ]);
      this.campaignPFijo.push(!powerTerms[ `pFijo_P${i + 1}` ] ? null : powerTerms[ `pFijo_P${i + 1}` ]);
    }
  }

  public calculateMarginMatchPrice(): void {
    this.showCalculation = true;
    // const inputsTE = this.pushInputTETP(this.renovateForm.get('calculateMargins.marginTE'));
    // const inputsTP = this.pushInputTETP(this.renovateForm.get('calculateMargins.marginTP'));
    // const sumTicketTE = [];
    // const sumTicketAccionaTE = [];
    // const sumTicketTP = [];
    // const sumTicketAccionaTP = [];
    // for (let i = 0; i < 6; i++) {
    //   const calcSelectTE = this.renovateForm.get('unitsTE').value !== '0' ?
    //     inputsTE[ i ] / this.renovateForm.get('unitsTE').value : inputsTE[ i ];
    //   const calcSelectTP = this.renovateForm.get('unitsTP').value !== '0' ?
    //     inputsTP[ i ] * this.renovateForm.get('unitsTP').value : inputsTP[ i ];
    //   const calcTicketTE = calcSelectTE * this.dataContract.campaign.consumptionEstimatedPeriod[ i ];
    //   const calcTicketAccionaTE = (this.campaignTE[ i ] / 1000) * this.dataContract.campaign.consumptionEstimatedPeriod[ i ];
    //   const calcTicketTP = calcSelectTP * this.dataContract.campaign.potencyContractedPeriod[ i ];
    //   const calcTicketAccionaTP = this.campaignTP[ i ] * this.dataContract.campaign.potencyContractedPeriod[ i ];
    //   sumTicketTE.push(calcTicketTE);
    //   sumTicketAccionaTE.push(calcTicketAccionaTE);
    //   sumTicketTP.push(calcTicketTP);
    //   sumTicketAccionaTP.push(calcTicketAccionaTP);
    // }
    // const ticketTE = sumTicketTE.reduce((total, val) => total + val, 0);
    // const ticketAccionaTE = sumTicketAccionaTE.reduce((total, val) => total + val, 0);
    // const ticketTP = sumTicketTP.reduce((total, val) => total + val, 0);
    // const ticketAccionaTP = sumTicketAccionaTP.reduce((total, val) => total + val, 0);
    // const sumRowEstimatedConsumption = this.dataContract.campaign.consumptionEstimatedPeriod.reduce((total, val) => total + val, 0);
    // const sumRowContractPotency = this.dataContract.campaign.potencyContractedPeriod.reduce((total, val) => total + val, 0);
    // const resultCalcTE = ((ticketTE - ticketAccionaTE) / sumRowEstimatedConsumption * 1000) / 1.015;
    // const resultCalcTP = (ticketTP - ticketAccionaTP) / sumRowContractPotency;
    // const commissionTE = ((((sumRowEstimatedConsumption / 1000) * this.commissionTE) / 100) * resultCalcTE);
    // const commissionTP = (((sumRowContractPotency * this.commissionTP) / 100) * resultCalcTP);
    // this.resultTE = [];
    // this.resultTP = [];
    // this.campaignTE.forEach((valTE, index) => {
    //   if (this.renovateForm.get('fee').value === '2.0TD' && index > 2) {
    //     this.resultTE.push(0);
    //   } else {
    //     const calc = ((resultCalcTE / 1000) * 1.015) + (valTE / 1000);
    //     this.resultTE.push(parseFloat(calc.toString().match(/^-?\d+(?:\.\d{0,6})?/)[ 0 ]));
    //   }
    // });
    // this.campaignTP.forEach((valTP, index) => {
    //   if (this.renovateForm.get('fee').value === '2.0TD' && index > 1) {
    //     this.resultTP.push(0);
    //   } else {
    //     const calc = (resultCalcTP * 1.015) + valTP;
    //     this.resultTP.push(parseFloat(calc.toString().match(/^-?\d+(?:\.\d{0,6})?/)[ 0 ]));
    //   }
    // });
    // const commissionFinal = commissionTE + commissionTP;
    // this.resultCalculation = commissionFinal >= 0;
    // this.renovateForm.get('enterMargins.marginsTE').setValue(parseFloat(String(resultCalcTE).match(/^-?\d+(?:\.\d{0,2})?/)[ 0 ]));
    // this.renovateForm.get('enterMargins.marginTP').setValue(parseFloat(String(resultCalcTP).match(/^-?\d+(?:\.\d{0,2})?/)[ 0 ]));
    // this.commission = parseFloat(String(commissionFinal).match(/^-?\d+(?:\.\d{0,2})?/)[ 0 ]);
    this.canSettlementsCommission();
    this.emitValuesSaveContact();
  }

  // private pushInputTETP(form: any): any {
  //   const inputs = [];
  //   Object.keys(form.controls).forEach(field => {
  //     inputs.push(form.get(field).value ? form.get(field).value.replace(',', '.') : form.get(field).value);
  //   });
  //   return inputs;
  // }

  private canSettlementsCommission(): void {
    if (!this.canSettlements && this.commission < 0) {
      this.toastrService.success('COMMONS.CALCULATES_INVALID');
    }
  }

  private emitValuesSaveContact(): void {
    const validSecondRadio = this.renovateForm.get('calculateMargins.marginTE')?.value >= 0 &&
      this.renovateForm.get('calculateMargins.marginTP')?.value >= 0 &&
      this.commission >= 0 &&
      this.showCalculation;
    const validFirstRadio = this.enterMarginsValid && this.commission >= 0;
    const validCampaignBase = this.renovateForm.get('marginIndicator').value === '2' ? validSecondRadio : validFirstRadio;
    const canSave = this.approved;
    const canGoNext = this.renovateForm.valid && this.approved &&
      !!this.renovateForm.get('town').value && !!this.renovateForm.get('province').value &&
      (this.renovateForm.get('campaignBase').value ? validCampaignBase : true);
    this.canSave.emit({ canGoNext, canSave, data: this.mapperSendData() });
  }

  private mapperSendData(): RenovateContractFormPymes {
    const data: RenovateContractFormPymes = this.renovateForm.getRawValue();
    if (this.townList?.length === 1) {
      data.town = this.townList[ 0 ].value;
    }
    if (!this.isUserSpanish) {
      data.phone = this.renovateForm.get('phoneCountryCode').value + data.phone;
      data.phoneSigner = this.renovateForm.get('phoneSignerCountryCode').value + data.phoneSigner;
      data.tension = this.dataContract.installation.tension;
      data.cycle = this.dataContract.installation.cycle;
      data.period = this.dataContract.installation.period;
      data.isMultibanco = this.renovateForm.get('payment')?.value === '1';
    }
    data.idDraft = this.idDraft;
    return data;
  }

  public onPasteIBAN(event: ClipboardEvent): void {
    const pasteIBAN: string = event.clipboardData.getData('text').replace(/\s/g, '');
    if (this.isUserSpanish && pasteIBAN && pasteIBAN.length === 24) {
      this.renovateForm.patchValue({
        iban: pasteIBAN.slice(0, 4),
        entity: pasteIBAN.slice(4, 8),
        office: pasteIBAN.slice(8, 12),
        dc: pasteIBAN.slice(12, 14),
        account: pasteIBAN.slice(14, 24)
      });
    } else if (!this.isUserSpanish && pasteIBAN && pasteIBAN.length === 25) {
      this.renovateForm.patchValue({
        iban: pasteIBAN.slice(0, 4),
        entity: pasteIBAN.slice(4, 8),
        office: pasteIBAN.slice(8, 12),
        account: pasteIBAN.slice(12, 23),
        dc: pasteIBAN.slice(23, 25)
      });
    }
  }

  public bankAccountKeyUp(
    event: KeyboardEvent,
    previousInputId: string | null,
    currentInputId: string,
    nextInputId: string | null
  ): void {
    const previousInput = this.getInputElement(previousInputId);
    const currentInput = this.getInputElement(currentInputId);
    const nextInput = this.getInputElement(nextInputId);
    if (!event.ctrlKey && event.key !== 'Control') {
      if (currentInput.selectionStart === currentInput.maxLength && nextInput) {
        nextInput.focus();
        nextInput.setSelectionRange(0, 0);
      } else if (
        (event.key === 'Backspace' || event.key === 'ArrowLeft')
        && currentInput.selectionStart === 0
        && previousInput
      ) {
        previousInput.focus();
      }
    }
  }

  private getInputElement(id: string): HTMLInputElement | null {
    return id ? (document.getElementById(id) as HTMLInputElement) : null;
  }

  private getMobileAndCountryCode(value: string | null): Mobile {
    const result: Mobile = { mobile: value };
    if (value) {
      if (!this.isUserSpanish && value?.length > 9) {
        const countryCode = value.slice(0, value.length - 9);
        const validCountryCode = this.mobileCountryCodeList.find(code => code.value === countryCode);
        if (validCountryCode) {
          result.countryCode = countryCode;
        }
      }
      result.mobile = value.slice(value.length - 9, value.length);
    }

    return result;
  }

  private initMobileCountryCodeConfigPortugal(): void {
    if (!this.isUserSpanish) {
      this.i18NextService.events.languageChanged.pipe(takeUntil(this.destroyed$)).subscribe(() => {
        this.mobileCountryCodeList = this.utilService.getListCountryMobile();
        this.createForm();
        this.initPostalCodePT();
      });
    }
  }

  private mobileCountryCodeChange(value: string, fieldName: string): void {
    if (value === '0034') {
      this.renovateForm.get(fieldName).setValidators(
        [this.utilValidatorsService.validateMobile, Validators.required, Validators.maxLength(9)]
      );
    } else if (value === '00351') {
      this.renovateForm.get(fieldName).setValidators(
        [this.utilValidatorsService.validateMobilePT, Validators.required, Validators.maxLength(9)]
      );
    }
    this.renovateForm.get(fieldName).updateValueAndValidity({ onlySelf: true });
  }

  private subscribeFormControlPortugal(): void {
    if (!this.isUserSpanish) {
      this.renovateForm.get('postalCodePT1').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
        this.renovateForm.get('postalCode').setValue(value + '-' + this.renovateForm.get('postalCodePT2').value);
      });
      this.renovateForm.get('postalCodePT2').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
        this.renovateForm.get('postalCode').setValue(this.renovateForm.get('postalCodePT1').value + '-' + value);
      });

      this.renovateForm.get('phoneCountryCode').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(
        value => {
          if (value) {
            this.mobileCountryCodeChange(value, 'phone');
          }
        }
      );
      this.renovateForm.get('phoneSignerCountryCode').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(
        value => {
          if (value) {
            this.mobileCountryCodeChange(value, 'phoneSigner');
          }
        }
      );
    }
  }

  private initPostalCodePT(): void {
    if (!this.isUserSpanish) {
      this.renovateForm.get('postalCodePT1').setValidators([Validators.required, Validators.maxLength(4)]);
      this.renovateForm.get('postalCodePT2').setValidators([Validators.required, Validators.maxLength(3)]);
      this.renovateForm.get('postalCode').setValidators([
        Validators.required,
        Validators.pattern(this.utilValidatorsService.postalCodePTPattern)
      ]);
    }
  }

  private validateIBANControlsPT(control: AbstractControl): ValidationErrors | null {
    const entity = control.get('entity').value;
    const office = control.get('office').value;
    const dc = control.get('dc').value;
    const account = control.get('account').value;
    if ((entity + office + dc + account) && !validateIBANPortugal(entity, office, dc, account)) {
      return { invalid_IBAN: true };
    }
    return null;
  }

  public getEnterMargins(enterMarginsForm: FormGroup): void {
    this.enterMarginsValid = enterMarginsForm.valid;
    if (this.isUserSpanish) {
      this.renovateForm.get('enterMargins').setValue({
        marginsTE: {
          p1: enterMarginsForm.get('marginTEP1')?.value?.toString().replace(',', '.') || null,
          p2: enterMarginsForm.get('marginTEP2')?.value?.toString().replace(',', '.') || null,
          p3: enterMarginsForm.get('marginTEP3')?.value?.toString().replace(',', '.') || null,
          p4: enterMarginsForm.get('marginTEP4')?.value?.toString().replace(',', '.') || null,
          p5: enterMarginsForm.get('marginTEP5')?.value?.toString().replace(',', '.') || null,
          p6: enterMarginsForm.get('marginTEP6')?.value?.toString().replace(',', '.') || null,
        },
        marginTP: enterMarginsForm.get('marginTP')?.value?.toString().replace(',', '.') || null,
      });
    } else {
      this.renovateForm.get('enterMargins').setValue({
        marginsTE: {
          p1: enterMarginsForm.get('marginTEP1')?.value?.toString().replace(',', '.') || null,
          p2: enterMarginsForm.get('marginTEP2')?.value?.toString().replace(',', '.') || null,
          p3: enterMarginsForm.get('marginTEP3')?.value?.toString().replace(',', '.') || null,
          p4: enterMarginsForm.get('marginTEP4')?.value?.toString().replace(',', '.') || null,
          p5: enterMarginsForm.get('marginTEP5')?.value?.toString().replace(',', '.') || null,
          p6: enterMarginsForm.get('marginTEP6')?.value?.toString().replace(',', '.') || null,
        }
      });
    }
    this.emitValuesSaveContact();
  }

  private subscribePaymentChanges(): void {
    this.renovateForm.get('payment').valueChanges.pipe(takeUntil(this.destroyed$)).subscribe(value => {
      if (value === '1') {
        this.renovateForm.get('iban').reset();
        this.renovateForm.get('entity').reset();
        this.renovateForm.get('office').reset();
        this.renovateForm.get('account').reset();
        this.renovateForm.get('dc').reset();
      }
      this.showBankAccount(value === '0' && this.renovateForm.get('campaign')?.value);
    });
  }

  private enablePayment(enable: boolean): void {
    enable ? this.renovateForm.get('payment').enable() : this.renovateForm.get('payment').disable();
  }

  private showBankAccount(show: boolean): void {
    const enableBankAccount = this.renovateForm.get('payment').value === '0';
    enableBankAccount ? this.renovateForm.get('iban').enable() : this.renovateForm.get('iban').disable();
    enableBankAccount ? this.renovateForm.get('entity').enable() : this.renovateForm.get('entity').disable();
    enableBankAccount ? this.renovateForm.get('office').enable() : this.renovateForm.get('office').disable();
    enableBankAccount ? this.renovateForm.get('dc').enable() : this.renovateForm.get('dc').disable();
    enableBankAccount ? this.renovateForm.get('account').enable() : this.renovateForm.get('account').disable();
    this.showBankAccountFields = show;
    this.renovateForm.get('iban').updateValueAndValidity();
    this.renovateForm.get('entity').updateValueAndValidity();
    this.renovateForm.get('office').updateValueAndValidity();
    this.renovateForm.get('account').updateValueAndValidity();
    this.renovateForm.get('dc').updateValueAndValidity();
    this.renovateForm.setValidators(show ? this.validateIBANControlsPT : null);
  }
}
